// ═══════════════════════════════════════════════════
// Auth Store — Session, user, tokens, active property
// Registers token handlers with the API client on init
// ═══════════════════════════════════════════════════

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { registerAuthHandlers } from '@/lib/api';
import type { User, Property } from '@/types';

interface AuthState {
  // Session
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Active context
  activeProperty: Property | null;

  // Actions
  setSession: (user: User, accessToken: string, refreshToken: string) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setActiveProperty: (property: Property | null) => void;
  updateUser: (partial: Partial<User>) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true, // Start loading until we verify session
      activeProperty: null,

      setSession: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      setActiveProperty: (property) =>
        set({ activeProperty: property }),

      updateUser: (partial) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...partial } : null,
        })),

      setLoading: (loading) =>
        set({ isLoading: loading }),

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          activeProperty: null,
        });
        // Redirect handled by AuthGuard, not here
      },
    }),
    {
      name: 'proppulse-auth',
      partialize: (state) => ({
        // Persist both tokens so refresh survives page reload
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
      onRehydrate: () => {
        // After hydrating from localStorage, register with API client
        return (state) => {
          if (state) {
            registerAuthHandlers(
              () => state.accessToken,
              () => state.refreshToken,
              (access, refresh) => state.setTokens(access, refresh),
              () => state.logout(),
            );
          }
        };
      },
    },
  ),
);

// ── Initial Registration ─────────────────────────
// Also register immediately for the case where onRehydrate
// fires before the store is fully ready
registerAuthHandlers(
  () => useAuthStore.getState().accessToken,
  () => useAuthStore.getState().refreshToken,
  (access, refresh) => useAuthStore.getState().setTokens(access, refresh),
  () => useAuthStore.getState().logout(),
);
